import { type Theme, alpha } from '@mui/material';

/**
 * Returns an sx-compatible object that overrides react-big-calendar's default
 * CSS so it blends with the Cadence MUI theme (dark or light).
 */
export function getCalendarStyles(theme: Theme) {
  return {
    // Hide built-in toolbar — we render our own header bar
    '.rbc-toolbar': {
      display: 'none',
    },

    // Root calendar container
    '.rbc-calendar': {
      background: 'transparent',
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
    },

    // Column / day headers
    '.rbc-header': {
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: '8px 4px',
      fontWeight: 600,
      fontSize: '0.8rem',
      textTransform: 'uppercase' as const,
    },
    '.rbc-header + .rbc-header': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },

    // Today highlight
    '.rbc-today': {
      background: alpha(theme.palette.primary.main, 0.08),
    },

    // Off-range (days from prev/next month in month view)
    '.rbc-off-range-bg': {
      background: alpha(theme.palette.background.default, 0.5),
    },
    '.rbc-off-range': {
      color: theme.palette.text.secondary,
    },

    // Month view cells
    '.rbc-month-view': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
    },
    '.rbc-month-row': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-month-row + .rbc-month-row': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-day-bg': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-day-bg + .rbc-day-bg': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },

    // Date cell numbers
    '.rbc-date-cell': {
      color: theme.palette.text.primary,
      padding: '4px 8px',
      fontSize: '0.8rem',
      '& a': {
        color: 'inherit',
      },
    },

    // Time slots (week/day views)
    '.rbc-time-view': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
    },
    '.rbc-time-header': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-time-header-content': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-time-content': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-time-content > * + * > *': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-timeslot-group': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-time-slot': {
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
    },
    '.rbc-time-gutter': {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
    },
    '.rbc-label': {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      padding: '0 8px',
    },

    // Current time indicator
    '.rbc-current-time-indicator': {
      background: theme.palette.error.main,
      height: 2,
    },

    // Event styling is handled by eventPropGetter, but some overrides:
    '.rbc-event': {
      borderRadius: 4,
      fontSize: '0.8rem',
    },
    '.rbc-event-label': {
      fontSize: '0.75rem',
    },
    '.rbc-event.rbc-selected': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },

    // "show more" link in month view
    '.rbc-show-more': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: '0.75rem',
      background: 'transparent',
    },

    // All-day row
    '.rbc-allday-cell': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '.rbc-row-segment': {
      padding: '0 2px',
    },

    // Agenda view
    '.rbc-agenda-view': {
      color: theme.palette.text.primary,
    },
    '.rbc-agenda-table': {
      color: theme.palette.text.primary,
      '& thead > tr > th': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: '0.8rem',
      },
      '& tbody > tr > td': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: '8px 12px',
      },
      '& tbody > tr + tr': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
    },

    // Date header in time view
    '.rbc-time-header-cell': {
      minHeight: 40,
    },
    '.rbc-button-link': {
      color: theme.palette.text.primary,
    },
  } as const;
}
